import { useEffect, useMemo, useRef, useState } from "react";
import UploadIcon from "../../../../assets/icons/upload.svg";
import RemoveIcon from "../../../../assets/icons/remove.svg";
import Dropzone, { IDropzoneProps } from "react-dropzone-uploader";
import Select from "react-select";
import { WithContext as ReactTags } from "react-tag-input";
import { AlertMsg } from "../../../../shared/components/alert-msg";
import "./ImageDrawer.scss";

const sizeofFetch = 20;
const DRAWER_CLOSE_DELAY = 260;

const getMoreImages = async ({ pageProps, payload }: any) => {
  await pageProps.getImageGalleryAction(payload, true);
};

const getImagesGallery = async ({ pageProps, payload }: any) => {
  await pageProps.getImageGalleryAction(payload, false);
};

type ImageDrawerProps = {
  pageProps: any;
  showDrawer: boolean;
  catListItems: any[];
  onClose: () => void;
  selectedImages: any[];
  onApply: (images: any[]) => void;
  maxImages?: number;
  applyButtonLabel?: string;
  title?: string;
};

export const ImageDrawer = ({
  pageProps,
  showDrawer,
  catListItems,
  onClose,
  selectedImages,
  onApply,
  maxImages,
  applyButtonLabel = "Add Images",
  title = "Add product image",
}: ImageDrawerProps) => {
  const [shouldRender, setShouldRender] = useState(showDrawer);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<"gallery" | "upload">("gallery");
  const [categoryChosen, setCategoryChosen] = useState<any>();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryToUpload, setUploadCategory] = useState<any>();
  const [productUploadOtherNames, setUploadProductOtherNames] = useState<any[]>([]);
  const [uploadOtherNamesTags, setUploadOtherNamesTags] = useState<any[]>([]);
  const [productNameError, setProductNameError] = useState(true);
  const [productCatError, setProductCatError] = useState(true);
  const [drawerImages, setDrawerImages] = useState<any[]>([]);
  const [showUploadAnother, setShowUploadAnother] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isFetchingMoreImages, setIsFetchingMoreImages] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const [uploadFeedback, setUploadFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const productUploadNameRef = useRef<any>(null);
  const hasFetchedOnOpen = useRef(false);
  const isUploadInFlightRef = useRef(false);
  const selectedImagesSignatureRef = useRef("");
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasImageLimit = typeof maxImages === "number" && maxImages > 0;
  const hasReachedMaxSelection =
    hasImageLimit && drawerImages.length >= (maxImages as number);

  useEffect(() => {
    if (showDrawer) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setShouldRender(true);
      setIsClosing(false);
      return;
    }

    if (!shouldRender) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, DRAWER_CLOSE_DELAY);

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [showDrawer, shouldRender]);

  useEffect(() => {
    if (!shouldRender) {
      hasFetchedOnOpen.current = false;
      selectedImagesSignatureRef.current = "";
      setGalleryPage(1);
      return;
    }

    document.body.style.overflow = "hidden";

    const normalizedSelectedImages = (selectedImages || [])
      .filter((image: any) => image?.url)
      .slice(0, hasImageLimit ? maxImages : undefined);
    const nextSignature = normalizedSelectedImages
      .map((image: any) => image.url)
      .join("|");

    if (selectedImagesSignatureRef.current !== nextSignature) {
      selectedImagesSignatureRef.current = nextSignature;
      setDrawerImages(normalizedSelectedImages);
    }

    if (!hasFetchedOnOpen.current) {
      hasFetchedOnOpen.current = true;
      setGalleryPage(1);
      setUploadFeedback(null);
      getImagesGallery({
        pageProps,
        payload: `page=1&size=${sizeofFetch}`,
      });
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [shouldRender, selectedImages, hasImageLimit, maxImages, pageProps]);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [shouldRender, onClose]);

  useEffect(() => {
    if (categoryToUpload && productCatError) {
      setProductCatError(false);
    }
  }, [categoryToUpload, productCatError]);

  useEffect(() => {
    if (!isFetchingMoreImages) {
      return;
    }

    const requestStatus = pageProps.getImageGalleryRequest?.request_status;

    if (
      requestStatus === "GET_IMAGE_GALLERY_NEWBATCH" ||
      requestStatus === "GET_IMAGE_GALLERY_FAILURE"
    ) {
      setIsFetchingMoreImages(false);
    }
  }, [
    isFetchingMoreImages,
    pageProps.getImageGalleryRequest?.request_status,
    pageProps.getImageGalleryRequest?.request_data,
    pageProps.getImageGalleryRequest?.galleryData?.length,
  ]);

  const galleryImages = pageProps.getImageGalleryRequest?.galleryData ?? [];
  const galleryResponse = pageProps.getImageGalleryRequest?.request_data?.response;
  const galleryTotalRecords = Number(galleryResponse?.totalNumberOfRecords ?? 0);
  const hasMoreGalleryImages =
    galleryTotalRecords > 0 ? galleryImages.length < galleryTotalRecords : false;

  const selectedImageUrls = useMemo(
    () => new Set(drawerImages.map((image: any) => image?.url).filter(Boolean)),
    [drawerImages],
  );

  const handleDelete = (index: number) => {
    setUploadOtherNamesTags((previous) => previous.filter((_, tagIndex) => tagIndex !== index));
    setUploadProductOtherNames((previous) => previous.filter((_, tagIndex) => tagIndex !== index));
  };

  const handleAddition = (tag: any) => {
    if (productUploadOtherNames.indexOf(tag?.text) === -1) {
      setUploadOtherNamesTags((previous) => [...previous, tag]);
      setUploadProductOtherNames((previous) => [...previous, tag?.text]);
    }
  };

  const transformImageName = (image: any) => {
    let filename = image;

    if (filename.indexOf("upload-file/") > -1) {
      filename = image.split("upload-file/")[1];
    }
    if (filename.indexOf("thumbnails/") > -1) {
      filename = image.split("thumbnails/")[1];
    }

    return filename;
  };

  const removeImageFromDrawer = (chosenImg: any) => {
    setDrawerImages((previous) => previous.filter((itemData: any) => itemData.url !== chosenImg.url));
  };

  const saveSearchFilter = (term: string) => {
    const payload = `page=1&size=${sizeofFetch}${term ? `&fileName=${term}` : ""}`;
    setGalleryPage(1);
    setIsFetchingMoreImages(false);
    getImagesGallery({ pageProps, payload });
  };

  const saveChosenCat = (event: any) => {
    const chosenValue = event.target.value;
    const chosenCat = catListItems.find((item: any) => `${item.value}` === chosenValue) ?? null;
    setSearchTerm("");
    setCategoryChosen(chosenCat);
    const payload = `page=1&size=${sizeofFetch}${chosenCat?.label ? `&fileName=${chosenCat.label}` : ""}`;
    setGalleryPage(1);
    setIsFetchingMoreImages(false);
    getImagesGallery({ pageProps, payload });
  };

  const saveUploadCat = (chosenCat: any) => {
    setUploadCategory(chosenCat);
  };

  const KeyCodes = {
    comma: 188,
    enter: 13,
  };

  const delimiters = [KeyCodes.comma, KeyCodes.enter];

  const handleChangeStatus: IDropzoneProps["onChangeStatus"] = () => {};

  const handleSubmit: IDropzoneProps["onSubmit"] = async (files: any, allFiles: any) => {
    if (
      isUploadInFlightRef.current ||
      isUploadingImage ||
      productNameError ||
      productCatError ||
      hasReachedMaxSelection
    ) {
      allFiles.forEach((file: any) => file.remove());
      return;
    }

    const selectedFile = files?.[0]?.file;

    if (!selectedFile) {
      allFiles.forEach((file: any) => file.remove());
      return;
    }

    const imageUploadData = new FormData();
    imageUploadData.append("ProductName", productUploadNameRef?.current?.value ?? "");
    imageUploadData.append("CategoryName", categoryToUpload.value);
    imageUploadData.append("ImageFile", selectedFile);
    imageUploadData.append("OtherNames", productUploadOtherNames as any);

    isUploadInFlightRef.current = true;
    setIsUploadingImage(true);
    setUploadFeedback(null);

    try {
      const response = await pageProps.uploadProductImageAction(
        imageUploadData,
        productUploadOtherNames,
      );

      if (response?.fileURL) {
        const nextImage = {
          index: drawerImages.length + 1,
          url: response.fileURL,
          otherNames: [...(response?.otherNames ?? [])],
        };

        setDrawerImages((previous) => {
          if (hasImageLimit && previous.length >= (maxImages as number)) {
            return previous;
          }

          return [...previous, nextImage];
        });
        setShowUploadAnother(true);
        setUploadFeedback({
          type: "success",
          message: "Image successfully uploaded.",
        });
      } else {
        setUploadFeedback({
          type: "error",
          message: "Image couldn't be uploaded.",
        });
      }
    } catch (error) {
      setUploadFeedback({
        type: "error",
        message: "Image couldn't be uploaded.",
      });
    } finally {
      isUploadInFlightRef.current = false;
      setIsUploadingImage(false);
      allFiles.forEach((file: any) => file.remove());
    }
  };

  const toggleGalleryImage = (image: any) => {
    if (selectedImageUrls.has(image.imageUrl)) {
      setDrawerImages((previous) => previous.filter((item) => item.url !== image.imageUrl));
      return;
    }

    if (hasReachedMaxSelection) {
      return;
    }

    setDrawerImages((previous) => [
      ...previous,
      {
        index: previous.length + 1,
        url: image.imageUrl,
        otherNames: image?.otherNames ? [...image.otherNames] : [],
      },
    ]);
  };

  const loadMoreGalleryImages = () => {
    if (isFetchingMoreImages || !hasMoreGalleryImages) {
      return;
    }

    const nextPage = galleryPage + 1;
    let payload = `page=${nextPage}&size=${sizeofFetch}`;

    if (categoryChosen?.label) {
      payload = `${payload}&fileName=${categoryChosen.label}`;
    }

    if (searchTerm.trim()) {
      payload = `${payload}&fileName=${searchTerm.trim()}`;
    }

    setIsFetchingMoreImages(true);
    setGalleryPage(nextPage);
    getMoreImages({ pageProps, payload });
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <div
        className={
          isClosing
            ? "variant-image-drawer-backdrop is-closing"
            : "variant-image-drawer-backdrop is-open"
        }
        onClick={onClose}
      />
      <aside className={isClosing ? "variant-image-drawer is-closing" : "variant-image-drawer is-open"}>
        <div className="variant-image-drawer-header">
          <div className="variant-image-drawer-title">{title}</div>
          <button type="button" className="variant-image-drawer-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="variant-image-drawer-tabs">
          <button
            type="button"
            className={activeTab === "upload" ? "active" : ""}
            onClick={() => setActiveTab("upload")}
          >
            Upload Image
          </button>
          <button
            type="button"
            className={activeTab === "gallery" ? "active" : ""}
            onClick={() => setActiveTab("gallery")}
          >
            MamiMarket Gallery
          </button>
        </div>

        <div className="variant-image-drawer-body">
          {activeTab === "upload" ? (
            <div>
              <div className="product-info image-info-details">
                <div className="two-sided">
                  <div className="form-group">
                    <input
                      placeholder="Product name"
                      onBlur={() => {
                        if ((productUploadNameRef?.current?.value ?? "").length < 1) {
                          setProductNameError(true);
                        } else {
                          setProductNameError(false);
                        }
                      }}
                      type="text"
                      ref={productUploadNameRef}
                      className="input-wrap"
                    />
                  </div>

                  <div className="form-group select-cat-wrap">
                    <Select
                      value={categoryToUpload}
                      onChange={saveUploadCat}
                      options={catListItems}
                      placeholder="Category"
                      className="select-wrap"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <ReactTags
                    delimiters={delimiters}
                    tags={uploadOtherNamesTags}
                    handleDelete={handleDelete}
                    handleAddition={handleAddition}
                    classNames={{
                      tagInput: "tagInputClass",
                      tagInputField: "input-wrap",
                    }}
                    placeholder="Other Names of this upload. e.g: Name1, Name2, Name3"
                  />
                  <span className="info-txt">**Type Other Name and press enter</span>
                </div>
              </div>

              {(productNameError || productCatError) && (
                <div className="form-input-error text-center">
                  Provide product image name and category before upload
                </div>
              )}

              {uploadFeedback ? (
                <div
                  className={
                    uploadFeedback.type === "success"
                      ? "variant-upload-feedback success"
                      : "variant-upload-feedback error"
                  }
                >
                  {uploadFeedback.message}
                </div>
              ) : null}

              <div
                className={
                  !productUploadNameRef?.current?.value || productCatError || hasReachedMaxSelection
                    ? "upload_img_wrap disabled_wrap"
                    : "upload_img_wrap"
                }
              >
                <Dropzone
                  onChangeStatus={handleChangeStatus}
                  onSubmit={handleSubmit}
                  accept="image/*"
                  maxFiles={1}
                  multiple={false}
                  canRestart={false}
                  canCancel={false}
                  autoUpload={false}
                  submitButtonContent={isUploadingImage ? "Uploading..." : "Upload image"}
                  inputContent={() => (
                    <div className="upload_cnt upload_msg" key={30}>
                      <img src={UploadIcon} alt="" />
                      {!showUploadAnother && <div className="upload-txt">Upload photo here</div>}
                      {showUploadAnother && <div className="upload-txt">Upload another photo here</div>}
                      <div className="or-txt">or</div>
                      <div className="upload-cta">Browse photo</div>
                    </div>
                  )}
                  styles={{ dropzone: { minHeight: 200, maxHeight: 250 } }}
                />
              </div>
            </div>
          ) : (
            <div className="search-gallery">
              <div className="gallery-toolbar">
                <div className="variant-gallery-search">
                  <input
                    type="text"
                    placeholder="Search gallery images"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter") {
                        saveSearchFilter(e.target.value.trim());
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="variant-gallery-search-button"
                    onClick={() => saveSearchFilter(searchTerm.trim())}
                  >
                    Search
                  </button>
                </div>
                <div className="filter-wrap">
                  <div className="filter-options native-select-wrap">
                    <select
                      id="variant-image-category-filter"
                      value={categoryChosen?.value ?? ""}
                      onChange={saveChosenCat}
                    >
                      <option value="">All categories</option>
                      {catListItems.map((categoryOption: any) => (
                        <option key={categoryOption.value} value={categoryOption.value}>
                          {categoryOption.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {(galleryImages && galleryImages.length >= 1) && (
                <div className="gallery-results-wrap">
                  <div className="gallery-head">
                    <h3>MamiMarket Gallery</h3>
                    <div className="gallery-txt">Choose from thousands of stunning images..</div>
                  </div>
                  <div id="variant-image-scrollable" className="variant-image-scrollable">
                    <div className="variant-image-grid-gallery">
                      {galleryImages.map((image: any, index: any) => (
                        <button
                          type="button"
                          className={
                            selectedImageUrls.has(image.imageUrl)
                              ? "each-image chosen-image"
                              : "each-image"
                          }
                          key={index}
                          onClick={() => toggleGalleryImage(image)}
                        >
                          <img src={image.imageUrl} alt="" className="gallery-img" />
                        </button>
                      ))}
                    </div>
                    {isFetchingMoreImages && (
                      <div className="variant-image-grid-shimmer" aria-hidden="true">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div className="variant-image-shimmer-card" key={index}>
                            <div className="variant-image-shimmer-block variant-image-shimmer-photo" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasMoreGalleryImages && (
                    <div className="variant-image-load-more-wrap">
                      <button
                        type="button"
                        className="variant-image-load-more-btn"
                        onClick={loadMoreGalleryImages}
                        disabled={isFetchingMoreImages}
                      >
                        {isFetchingMoreImages ? "Loading..." : "Load more"}
                      </button>
                    </div>
                  )}
                  {!hasMoreGalleryImages && galleryImages.length >= 1 && (
                    <div className="variant-image-load-more-end">All images loaded</div>
                  )}
                </div>
              )}
              {pageProps.getImageGalleryRequest.request_status === "GET_IMAGE_GALLERY_PENDING" && (
                <div className="variant-image-grid-shimmer">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div className="variant-image-shimmer-card" key={index}>
                      <div className="variant-image-shimmer-block variant-image-shimmer-photo" />
                    </div>
                  ))}
                </div>
              )}
              {pageProps.getImageGalleryRequest.request_status === "GET_IMAGE_GALLERY_FAILURE" && (
                <AlertMsg
                  type="error"
                  message={pageProps.getImageGalleryRequest?.request_data?.error}
                />
              )}
            </div>
          )}
        </div>

        <div className="variant-image-drawer-footer">
          {drawerImages.length >= 1 && (
            <div className="variant-image-selected-panel">
              {drawerImages.map((eachImage: any, index: number) => (
                <div className="variant-image-selected-item" key={`${eachImage.url}-${index}`}>
                  <div className="variant-image-selected-url">{transformImageName(eachImage.url)}</div>
                  <button
                    type="button"
                    className="variant-image-selected-remove"
                    onClick={() => removeImageFromDrawer(eachImage)}
                  >
                    <img src={RemoveIcon} alt="" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="variant-image-footer-actions">
            <div className="variant-image-selected-count">
              {hasImageLimit ? `${drawerImages.length}/${maxImages} selected` : `${drawerImages.length} selected`}
            </div>
            <button
              type="button"
              className="variant-image-apply-btn"
              onClick={() => onApply(drawerImages)}
            >
              {applyButtonLabel}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
